import React from "react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { formatDateShort } from "@/lib/dateFormat";

export const LOAD_WARN = 80;
export const LOAD_OVER = 100;
const BAND = { warn: "#FACC15", over: "#EF233C" };

export const loadLevel = (pct) => (pct >= LOAD_OVER ? "over" : pct >= LOAD_WARN ? "warn" : "ok");

const AmpelLegend = ({ t }) => (
  <div className="mt-1 flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground" data-testid="forecast-ampel-legend">
    <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm" style={{ background: BAND.warn, opacity: 0.5 }} />{t("forecast_load_warn")}</span>
    <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm" style={{ background: BAND.over, opacity: 0.4 }} />{t("forecast_load_over")}</span>
  </div>
);

export const ForecastChart = ({ series, t, testId = "forecast-chart" }) => {
  const data = (series || []).map((row) => {
    const pool = row.booked_pool_m3 ?? row.booked_m3;
    const large = row.booked_large_m3 ?? 0;
    const pct = row.quota_m3 > 0 ? Math.round(((pool + large) / row.quota_m3) * 100) : 0;
    return { d: formatDateShort(row.date), pool, large, pending: row.pending_m3 ?? 0, quota: row.quota_m3, pct, level: loadLevel(pct) };
  });
  if (data.length === 0) {
    return <div className="grid h-64 place-items-center text-sm text-muted-foreground">{t("no_data")}</div>;
  }
  const byLabel = Object.fromEntries(data.map((r) => [r.d, r]));
  const flagged = data.filter((r) => r.level !== "ok");
  return (
    <div data-testid={testId}>
      <div style={{ width: "100%", height: 380 }}>
        <ResponsiveContainer>
          <ComposedChart data={data}>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
            <XAxis dataKey="d" stroke="hsl(var(--muted-foreground))" fontSize={12} />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
            {flagged.map((r) => (
              <ReferenceArea key={r.d} x1={r.d} x2={r.d} fill={BAND[r.level]} fillOpacity={r.level === "over" ? 0.22 : 0.3}
                stroke="none" ifOverflow="extendDomain" />
            ))}
            <Tooltip
              formatter={(v) => `${Number(v).toLocaleString("de-DE", { maximumFractionDigits: 1 })} m³`}
              labelFormatter={(l) => `${l} · ${t("forecast_load")} ${byLabel[l]?.pct ?? 0} %`}
              contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }}
            />
            <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            <Bar dataKey="pool" stackId="booked" name={t("forecast_booked_pool")} fill="#87986A" />
            <Bar dataKey="large" stackId="booked" name={t("forecast_booked_large")} fill="#F4A259" radius={[4, 4, 0, 0]} />
            <Line type="monotone" dataKey="pending" name={t("forecast_pending")} stroke="#94A3B8" strokeOpacity={0.8} strokeDasharray="2 3" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="quota" name={t("total_quota")} stroke="#EF233C" strokeDasharray="4 4" strokeWidth={1.5} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <AmpelLegend t={t} />
      <div className="sr-only" data-testid={`${testId}-flags`}>
        {flagged.map((r) => `${r.d}:${r.level}:${r.pct}`).join(",")}
      </div>
    </div>
  );
};
